import api from './api';  // Existing axios instance with auth headers
import { VinDecodeResult, VehicleUrlConfig, OemVehicleConfig, OemCategoryResult, OemDiagramResult, OemPartResult, BrandMapping, OemPartRequest, OemMarkupSetting, OemStats, SharedCatalogPart, InteractiveDiagramData, SharedCatalogStats, DiagramHotspot } from '../types/oem-parts';

const BASE = '/oem-parts';

export const oemPartsApi = {
  // VIN decode
  decodeVin: async (vin: string): Promise<{ decoded: VinDecodeResult; vehicleUrl: VehicleUrlConfig | null; vehicleConfigId: number | null }> => {
    const { data } = await api.post(`${BASE}/decode-vin`, { vin });
    return data.data;
  },

  // Manual vehicle selection
  selectVehicle: async (year: number, make: string, model: string, trim?: string, engine?: string) => {
    const { data } = await api.post(`${BASE}/select-vehicle`, { year, make, model, trim, engine });
    return data.data;
  },

  // Get supported brands
  getSupportedMakes: async (): Promise<BrandMapping[]> => {
    const { data } = await api.get(`${BASE}/supported-makes`);
    return data.data;
  },

  // Get categories for vehicle
  getCategories: async (vehicleConfigId: number): Promise<OemCategoryResult[]> => {
    const { data } = await api.get(`${BASE}/categories/${vehicleConfigId}`);
    return data.data;
  },

  // Get diagram with parts
  getDiagram: async (diagramId: number): Promise<OemDiagramResult> => {
    const { data } = await api.get(`${BASE}/diagram/${diagramId}`);
    return data.data;
  },

  // Part lookup
  lookupPart: async (params: { vehicle_config_id?: number; part_number?: string; description?: string }) => {
    const { data } = await api.post(`${BASE}/lookup`, params);
    return data.data;
  },

  // Search cached parts
  searchParts: async (q: string, vehicleConfigId?: number, limit?: number): Promise<OemPartResult[]> => {
    const params = new URLSearchParams({ q });
    if (vehicleConfigId) params.set('vehicle_config_id', String(vehicleConfigId));
    if (limit) params.set('limit', String(limit));
    const { data } = await api.get(`${BASE}/search?${params}`);
    return data.data;
  },

  // Add parts to work order
  addToOrder: async (workOrderId: number, lineItemId: number, parts: Array<{ oem_part_id: number; quantity: number; use_our_price: boolean }>) => {
    const { data } = await api.post(`${BASE}/add-to-order`, { work_order_id: workOrderId, line_item_id: lineItemId, parts });
    return data.data;
  },

  // Tech request part
  requestPart: async (params: { work_order_id: number; line_item_id: number; oem_part_id: number; quantity: number; reason?: string }) => {
    const { data } = await api.post(`${BASE}/request-part`, params);
    return data.data;
  },

  // Resolve confidence
  resolveConfidence: async (lookupId: number, selectedOptionIndex: number) => {
    const { data } = await api.post(`${BASE}/resolve-confidence`, { lookup_id: lookupId, selected_option_index: selectedOptionIndex });
    return data.data;
  },

  // Part requests (advisor/owner)
  getPartRequests: async (status?: string, workOrderId?: number): Promise<OemPartRequest[]> => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (workOrderId) params.set('work_order_id', String(workOrderId));
    const { data } = await api.get(`${BASE}/part-requests?${params}`);
    return data.data;
  },

  // Approve/deny request
  updatePartRequest: async (requestId: number, action: 'approve' | 'deny', options?: { denial_reason?: string; notes?: string }) => {
    const { data } = await api.put(`${BASE}/part-requests/${requestId}`, { action, ...options });
    return data.data;
  },

  // Stats (owner)
  getStats: async (): Promise<OemStats> => {
    const { data } = await api.get(`${BASE}/stats`);
    return data.data;
  },

  // Markup settings
  getMarkupSettings: async (): Promise<OemMarkupSetting[]> => {
    const { data } = await api.get(`${BASE}/markup-settings`);
    return data.data;
  },

  createMarkupSetting: async (setting: Partial<OemMarkupSetting>) => {
    const { data } = await api.post(`${BASE}/markup-settings`, setting);
    return data.data;
  },

  updateMarkupSetting: async (id: number, updates: Partial<OemMarkupSetting>) => {
    const { data } = await api.put(`${BASE}/markup-settings/${id}`, updates);
    return data.data;
  },

  deleteMarkupSetting: async (id: number) => {
    const { data } = await api.delete(`${BASE}/markup-settings/${id}`);
    return data.data;
  },

  // Diagram image URL helper
  getDiagramImageUrl: (diagramId: number): string => {
    return `${BASE}/diagram-image/${diagramId}`;
  },

  // ── Shared Catalog ──────────────────────────────────────────────────────

  getSharedCatalog: async (page = 1, limit = 50): Promise<{ parts: SharedCatalogPart[]; total: number }> => {
    const { data } = await api.get(`${BASE}/shared-catalog`, { params: { page, limit } });
    return { parts: data.data, total: data.total };
  },

  searchSharedCatalog: async (q: string, filters?: { make?: string; model?: string; year?: number; category?: string; page?: number; limit?: number }): Promise<{ parts: SharedCatalogPart[]; total: number }> => {
    const params: Record<string, any> = { q, ...filters };
    const { data } = await api.get(`${BASE}/shared-catalog/search`, { params });
    return { parts: data.data, total: data.total };
  },

  getSharedCatalogStats: async (): Promise<SharedCatalogStats> => {
    const { data } = await api.get(`${BASE}/shared-catalog/stats`);
    return data.data;
  },

  getDiagramWithHotspots: async (diagramId: number): Promise<InteractiveDiagramData> => {
    const { data } = await api.get(`${BASE}/shared-catalog/diagram/${diagramId}`);
    return data.data;
  },

  setDiagramHotspots: async (diagramId: number, hotspots: Array<{ callout_number: string; x_percent: number; y_percent: number; width_percent?: number; height_percent?: number }>): Promise<DiagramHotspot[]> => {
    const { data } = await api.put(`${BASE}/shared-catalog/diagram/${diagramId}/hotspots`, { hotspots });
    return data.data;
  },

  importToSharedCatalog: async (vehicleConfigId: number, category: string): Promise<{ promoted_parts: number; message: string }> => {
    const { data } = await api.post(`${BASE}/shared-catalog/import`, { vehicleConfigId, category });
    return data.data;
  },
};
